// Helpers for pre-selecting a HIX client (company) from the SharePoint site title.
//
// The agreed site-title convention is "leading code then name", e.g.
//   "12345 - Acme Trading"   -> code "12345",  name "Acme Trading"
//   "12345_Acme Trading"     -> code "12345",  name "Acme Trading"
//   "Acme Trading"           -> no code,       name "Acme Trading"
//
// The extracted code is matched against a HIX company's internal_client_id; if that
// yields no match, the name is matched against the company name; if the full name
// returns nothing, each of its words is searched separately (in parallel) to at least
// seed suggestions. All lookups go through GET /api/v1/companies (the same endpoint
// that backs the dropdown).

import { IHixCompaniesResponse, IHixCompany } from "../../interfaces/IHix";

export interface IParsedSiteTitle {
  code?: string;
  name?: string;
}

// Leading alphanumeric token (optionally containing . _ - inside), then a - or _ delimiter
// surrounded by optional whitespace, then the rest as the name.
const LEADING_CODE_REGEX = /^\s*([A-Za-z0-9][A-Za-z0-9._-]*?)\s*[-_]\s*(.+)$/;

export const parseSiteTitle = (title?: string): IParsedSiteTitle => {
  if (!title || !title.trim()) {
    return {};
  }

  const trimmed = title.trim();
  const match = trimmed.match(LEADING_CODE_REGEX);

  if (match) {
    const code = match[1]?.trim();
    const name = match[2]?.trim();
    return {
      code: code || undefined,
      name: name || undefined,
    };
  }

  // No delimiter — treat the whole title as the client name.
  return { name: trimmed };
};

// Distinct words of a parsed name, for the per-word fallback searches that run when the
// full-name lookup returns nothing. Single-character tokens ("&", initials, stray "-")
// only produce noise pages, so they are dropped; repeated words are searched once.
export const splitNameWords = (name?: string): string[] => {
  if (!name || !name.trim()) {
    return [];
  }

  const seen = new Set<string>();
  const words: string[] = [];
  for (const word of name.trim().split(/\s+/)) {
    const key = word.toLowerCase();
    if (word.length < 2 || seen.has(key)) {
      continue;
    }
    seen.add(key);
    words.push(word);
  }
  return words;
};

// Runs one company search per word concurrently and merges the results, deduplicated by
// company id (the same company usually matches several words of its own name). A word
// search that fails resolves to nothing instead of sinking its siblings: ApiService
// rethrows non-auth errors, and an unguarded Promise.all would discard every other
// word's already-fetched companies over one failed request.
export const searchNameWords = async (
  words: string[],
  search: (word: string) => Promise<IHixCompaniesResponse | undefined>
): Promise<IHixCompany[]> => {
  const results = await Promise.all(words.map(word => search(word).catch(() => undefined)));

  const byId = new Map<number, IHixCompany>();
  results.forEach(result => (result?.data ?? []).forEach(c => byId.set(c.id, c)));
  return Array.from(byId.values());
};
